/**
 * Web chat API — thin wrapper over `@wg/api/chat` with wunderguide-web's
 * auth + http deps so callers don't need to know about transports. All
 * endpoint paths and response shaping live in the shared module. Same
 * endpoints as RN:
 *
 *   POST /chat/v1/{sessionId}/message   { message, history?, language }
 *     → JSON ChatResponse OR SSE stream depending on Accept header
 *   GET  /chat/v1/{sessionId}/messages?limit=&offset=
 *   GET  /functions/v1/chat?sessionId=...   (legacy merged voice+text)
 *
 * Response.content[] items are echoed back verbatim — UI decides how to
 * render text / card / image / loading / system, etc.
 */
import {
  sendChatMessage as sharedSendChatMessage,
  streamChatMessage as sharedStreamChatMessage,
  getChatMessages,
  getLegacyMergedHistory,
  bootstrapChatSession as sharedBootstrapChatSession,
  type BootstrapOptions,
  type BootstrapResult,
  type ChatHistoryItem,
  type ChatHistoryMessage,
  type ChatMessageItem,
  type SendChatMessageOptions,
  type SendChatMessageResponse,
  type StreamChatHandlers,
} from '@wg/api/chat';
import config from '../../config/env.json';
import { httpClient } from '../http';
import { auth } from '../client';

export type {
  BootstrapOptions,
  BootstrapResult,
  ChatHistoryItem,
  ChatHistoryMessage,
  ChatMessageItem,
  SendChatMessageOptions,
  SendChatMessageResponse,
  StreamChatHandlers,
};

export function sendChatMessage(
  sessionId: string,
  message: string,
  options: {
    history?: ChatHistoryMessage[];
    language?: string;
    image?: { data: string; mime?: string; url?: string };
  } = {},
): Promise<SendChatMessageResponse> {
  return sharedSendChatMessage(httpClient, sessionId, message, {
    history: options.history,
    language: options.language || 'en-US',
    image: options.image,
  });
}

/**
 * Stream a chat message via SSE. Wraps `@wg/api/chat` with this app's auth
 * and base URL so the UI just deals with handlers.
 */
export function streamChatMessage(
  sessionId: string,
  message: string,
  handlers: StreamChatHandlers,
  options: SendChatMessageOptions = {},
): Promise<void> {
  return sharedStreamChatMessage(
    {
      baseURL: config.BASE_URL,
      getAuthToken: () => auth.getAuthToken(),
      getSelectedLanguage: () =>
        typeof navigator !== 'undefined'
          ? navigator.language || 'en-US'
          : 'en-US',
    },
    sessionId,
    message,
    handlers,
    options,
  );
}

export async function getChatHistory(
  sessionId: string,
  limit = 30,
  offset = 0,
): Promise<{
  messages: ChatHistoryItem[];
  hasMore: boolean;
  totalCount: number;
}> {
  return getChatMessages(httpClient, sessionId, limit, offset);
}

/**
 * Fetch the merged voice + text history via the legacy edge endpoint
 * (`/functions/v1/chat?sessionId=...`), mirroring the RN client's
 * `useMessages.fetchMessages`. See `getLegacyMergedHistory` in @wg/api/chat.
 */
export async function getMergedHistory(
  sessionId: string,
  limit = 50,
  offset = 0,
): Promise<{ messages: ChatHistoryItem[]; hasMore: boolean }> {
  return getLegacyMergedHistory(httpClient, sessionId, limit, offset);
}

/**
 * Find or create a chat session for the current user. Same flow as
 * useAudioChat.bootstrapSession but reusable from the chat drawer. No
 * options — web uses the most-recent session + location-default agent.
 */
export async function bootstrapChatSession(): Promise<BootstrapResult | null> {
  return sharedBootstrapChatSession(httpClient);
}

export default {
  sendMessage: sendChatMessage,
  getHistory: getChatHistory,
  getMergedHistory,
};
